// src/pages/AdminPanel/Customers.jsx
import React, { useEffect, useState } from 'react';
import { db } from '../../services/firebase';
import { collection, getDocs, query, orderBy } from 'firebase/firestore'; // Added orderBy for consistency
import Swal from 'sweetalert2'; // For displaying customer details in a modal
import { Eye, Mail, User } from 'lucide-react'; // Icons
import "./Customers.css";

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCustomers = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch from 'users' collection to get all registered users
      // Ordering by creation date (most recent first) for better display
      const q = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q); 
      const customersData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCustomers(customersData);
    } catch (err) {
      console.error("Error fetching customers:", err);
      setError("Failed to load customer data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  const handleViewDetails = (customer) => {
    Swal.fire({
      title: 'Customer Details',
      html: `
        <div class="swal2-content-custom">
          <p><strong>Customer ID:</strong> ${customer.id}</p>
          <p><strong>Name:</strong> ${customer.name || 'N/A'}</p>
          <p><strong>Email:</strong> ${customer.email}</p>
          <p><strong>Role:</strong> ${customer.role || 'user'}</p>
          <p><strong>Joined Date:</strong> ${customer.createdAt ? new Date(customer.createdAt).toLocaleDateString() : 'N/A'}</p>
          <p><strong>Email Verified:</strong> ${customer.emailVerified ? 'Yes' : 'No'}</p>
          ${customer.phoneNumber ? `<p><strong>Phone:</strong> ${customer.phoneNumber}</p>` : ''}
          ${customer.address ? `<p><strong>Address:</strong> ${Object.values(customer.address).join(', ')}</p>` : ''}
        </div>
      `,
      showCloseButton: true,
      showConfirmButton: false,
      background: '#1a202c', // Dark background for Swal
      color: '#e2e8f0', // Light text for Swal
      customClass: {
        popup: 'swal2-popup-custom',
        title: 'swal2-title-custom',
        htmlContainer: 'swal2-html-container-custom'
      }
    });
  };

  if (loading) return <div className="text-center py-10 text-gray-300 text-xl">Loading Customers...</div>;
  if (error) return <div className="text-center py-10 text-red-400 text-xl">{error}</div>;

  return (
    <div className="customers-container-main"> {/* Main container for page-level styling */}
      <h2 className="section-title">Customer Management</h2> {/* Consistent title styling */}

      {customers.length === 0 ? (
        <div className="empty-state-message">No customers found.</div>
      ) : (
        <div className="customers-table-wrapper"> {/* Container for the table for shadow/border */}
          <table className="customers-table"> {/* The actual HTML table */}
            <thead>
              <tr className="table-header-row">
                <th className="px-6 py-3 text-left">Customer ID</th>
                <th className="px-6 py-3 text-left">Name</th>
                <th className="px-6 py-3 text-left">Email</th>
                <th className="px-6 py-3 text-left">Role</th>
                <th className="px-6 py-3 text-left">Joined Date</th>
                <th className="px-6 py-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {customers.map(customer => (
                <tr className="table-row" key={customer.id}>
                  <td className="px-6 py-4 customer-id-cell">{customer.id.slice(0, 8)}...</td> {/* Truncate ID */}
                  <td className="px-6 py-4">{customer.name || 'N/A'}</td>
                  <td className="px-6 py-4 customer-email-cell">{customer.email}</td>
                  <td className="px-6 py-4">{customer.role || 'user'}</td> {/* Default to 'user' role */}
                  <td className="px-6 py-4">
                    {customer.createdAt ? new Date(customer.createdAt).toLocaleDateString() : 'N/A'}
                  </td>
                  <td className="px-6 py-4 action-buttons-cell">
                    <button 
                      onClick={() => handleViewDetails(customer)}
                      className="view-details-button"
                    >
                      <Eye size={16} className="mr-1" /> View Details
                    </button>
                    <a 
                      href={`mailto:${customer.email}`} 
                      className="email-customer-button"
                      target="_blank" rel="noopener noreferrer" // For opening in new tab
                    >
                      <Mail size={16} className="mr-1" /> Email
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Customers;
